/**
 * LEAFUP SHIPPING CALCULATOR — FINAL v3 (delivery line last)
 * Shopify Basic / Rise 15.5
 *
 * Customer flow:
 *   PIN → India Post lookup → LeafUp zone → fixed delivery charge
 *
 * Rates:
 *   NCR                  ₹100
 *   Mumbai / Bengaluru   ₹200
 *   Other India          ₹300
 *   Northeast / J&K      ₹400
 *
 * IMPORTANT:
 * Create ONE Shopify product with handle:
 *   leafup-delivery-charge
 *
 * Create exactly these FOUR variants:
 *   1. Delhi NCR              — ₹100
 *   2. Mumbai & Bengaluru     — ₹200
 *   3. Other India            — ₹300
 *   4. Northeast & J&K        — ₹400
 *
 * The script automatically reads the variant IDs from the product.
 * You do NOT need to enter variant IDs into this file.
 *
 * The delivery-charge product should:
 *   - be Active / published to the Online Store
 *   - be hidden from normal collections/navigation
 *   - have inventory tracking OFF, or sufficient inventory
 *
 * No EC2.
 * No Delhivery API.
 * No Shopify Carrier Service.
 */

(function () {
  'use strict';

  const CONFIG = {
    deliveryProductHandle: 'leafup-delivery-charge',

    indiaPostUrl:
      'https://raw.githubusercontent.com/IndiaPost/pin/master/api/v01/json/{PIN}.json',

    cachePrefix: 'leafup_shipping_pin_',
    cacheDays: 30,

    rates: {
      NCR: 100,
      METRO: 200,
      DEFAULT: 300,
      REMOTE: 400
    },

    /*
     * Customer-facing estimated delivery time.
     * These are fixed LeafUp estimates by zone, not a live
     * carrier estimate.
     */
    deliveryDays: {
      NCR: '1-2 days',
      MUMBAI: '2-4 days',
      BENGALURU: '2-4 days',
      NORTHEAST: '3-5 days',
      JK: '4-6 days',
      DEFAULT: '3-5 days'
    },

    variantNames: {
      NCR: ['Delhi NCR', 'NCR'],
      METRO: ['Mumbai & Bengaluru', 'Mumbai/Bengaluru', 'Mumbai & Bangalore'],
      DEFAULT: ['Other India', 'Default', 'Tier 2/3', 'Other'],
      REMOTE: ['Northeast & J&K', 'Northeast/J&K', 'Northeast & Jammu & Kashmir']
    },

    ui: {
      wrapperId: 'leafup-shipping-calculator',
      inputId: 'leafup-shipping-pincode',
      buttonId: 'leafup-calculate-shipping',
      messageId: 'leafup-shipping-message',
      amountId: 'leafup-shipping-amount'
    }
  };

  /* ============================================================
     NCR DISTRICTS
     ============================================================ */

  const NCR_DISTRICTS = new Set([
    // Delhi
    'delhi',
    'new delhi',
    'central delhi',
    'east delhi',
    'north delhi',
    'north east delhi',
    'north west delhi',
    'south delhi',
    'south east delhi',
    'south west delhi',
    'west delhi',

    // Haryana
    'faridabad',
    'gurgaon',
    'gurugram',
    'nuh',
    'mewat',
    'rohtak',
    'sonipat',
    'sonepat',
    'rewari',
    'jhajjar',
    'panipat',
    'palwal',
    'bhiwani',
    'charkhi dadri',
    'mahendragarh',
    'jind',
    'karnal',

    // Uttar Pradesh
    'meerut',
    'ghaziabad',
    'gautam buddha nagar',
    'gautam budh nagar',
    'bulandshahr',
    'buland shahr',
    'baghpat',
    'hapur',
    'shamli',
    'muzaffarnagar',
    'muzaffar nagar',

    // Rajasthan
    'alwar',
    'bharatpur'
  ]);

  const NORTHEAST_STATES = new Set([
    'arunachal pradesh',
    'assam',
    'manipur',
    'meghalaya',
    'mizoram',
    'nagaland',
    'sikkim',
    'tripura'
  ]);

  /* ============================================================
     GENERAL HELPERS
     ============================================================ */

  function normalize(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[().,_-]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function cleanPin(value) {
    return String(value || '').replace(/\D/g, '').slice(0, 6);
  }

  function validPinFormat(pin) {
    return /^\d{6}$/.test(pin);
  }

  function money(amount) {
    return '₹' + Number(amount).toLocaleString('en-IN');
  }

  function shopifyRoot() {
    return (
      window.Shopify &&
      window.Shopify.routes &&
      window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/'
    );
  }

  /* ============================================================
     LOCAL CACHE
     ============================================================ */

  function cacheKey(pin) {
    return CONFIG.cachePrefix + pin;
  }

  function getCached(pin) {
    try {
      const raw = localStorage.getItem(cacheKey(pin));
      if (!raw) return null;

      const item = JSON.parse(raw);
      const maxAge = CONFIG.cacheDays * 24 * 60 * 60 * 1000;

      if (!item.time || Date.now() - item.time > maxAge) {
        localStorage.removeItem(cacheKey(pin));
        return null;
      }

      return item.data || null;
    } catch (error) {
      return null;
    }
  }

  function setCached(pin, data) {
    try {
      localStorage.setItem(
        cacheKey(pin),
        JSON.stringify({
          time: Date.now(),
          data: data
        })
      );
    } catch (error) {
      // Local storage can fail in private/restricted browsers.
    }
  }

  /* ============================================================
     INDIA POST LOOKUP
     ============================================================ */

  async function lookupPin(pin) {
    const cached = getCached(pin);
    if (cached) return cached;

    const url = CONFIG.indiaPostUrl.replace('{PIN}', pin);

    let response;

    try {
      response = await fetch(url, {
        method: 'GET',
        cache: 'no-store'
      });
    } catch (error) {
      /*
       * The public PIN dataset can be temporarily unavailable.
       * Do not block a customer in that situation. Use the
       * default Other India zone.
       */
      const fallback = {
        pin: pin,
        records: [],
        fallback: true
      };

      setCached(pin, fallback);
      return fallback;
    }

    /*
     * If this particular public dataset does not contain the PIN,
     * do not reject the customer. Use the default Other India
     * zone (₹300 / 3-5 days).
     */
    if (!response.ok) {
      const fallback = {
        pin: pin,
        records: [],
        fallback: true
      };

      setCached(pin, fallback);
      return fallback;
    }

    let records;

    try {
      records = await response.json();
    } catch (error) {
      const fallback = {
        pin: pin,
        records: [],
        fallback: true
      };

      setCached(pin, fallback);
      return fallback;
    }

    if (!Array.isArray(records) || records.length === 0) {
      const fallback = {
        pin: pin,
        records: [],
        fallback: true
      };

      setCached(pin, fallback);
      return fallback;
    }

    const usableRecords = records.filter(function (record) {
      return record && record.pincode === pin;
    });

    if (!usableRecords.length) {
      const fallback = {
        pin: pin,
        records: [],
        fallback: true
      };

      setCached(pin, fallback);
      return fallback;
    }

    const result = {
      pin: pin,
      records: usableRecords,
      fallback: false
    };

    setCached(pin, result);

    return result;
  }

  /* ============================================================
     ZONE CLASSIFICATION
     ============================================================ */

  function districtOf(record) {
    return normalize(record.Districtname);
  }

  function stateOf(record) {
    return normalize(record.statename);
  }

  function isNCRRecord(record) {
    return NCR_DISTRICTS.has(districtOf(record));
  }

  function isMumbaiRecord(record) {
    const district = districtOf(record);
    const state = stateOf(record);

    return (
      state === 'maharashtra' &&
      (
        district === 'mumbai' ||
        district === 'mumbai city' ||
        district === 'mumbai suburban' ||
        district === 'mumbai suburban district'
      )
    );
  }

  function isBengaluruRecord(record) {
    const district = districtOf(record);
    const state = stateOf(record);

    return (
      state === 'karnataka' &&
      (
        district === 'bangalore' ||
        district === 'bangalore urban' ||
        district === 'bengaluru' ||
        district === 'bengaluru urban'
      )
    );
  }

  function isJKRecord(record) {
    const state = stateOf(record);

    return (
      state === 'jammu and kashmir' ||
      state === 'jammu kashmir' ||
      state === 'jammu & kashmir'
    );
  }

  function isNortheastRecord(record) {
    return NORTHEAST_STATES.has(stateOf(record));
  }

  /*
   * IMPORTANT:
   * A single PIN can have several post offices/records.
   * We check ALL records rather than taking only the first one.
   *
   * Priority:
   *   NCR → ₹100
   *   Mumbai/Bengaluru → ₹200
   *   Northeast/J&K → ₹400
   *   Everything else → ₹300
   */
  function determineZone(lookupResult) {
    const records = lookupResult.records || [];

    if (records.some(isNCRRecord)) {
      return 'NCR';
    }

    if (records.some(isMumbaiRecord)) {
      return 'MUMBAI';
    }

    if (records.some(isBengaluruRecord)) {
      return 'BENGALURU';
    }

    if (records.some(isJKRecord)) {
      return 'JK';
    }

    if (records.some(isNortheastRecord)) {
      return 'NORTHEAST';
    }

    return 'DEFAULT';
  }

  /*
   * Mumbai and Bengaluru share the same ₹200 Shopify variant.
   * J&K and Northeast share the same ₹400 Shopify variant.
   */
  function chargeVariantZone(zone) {
    if (zone === 'MUMBAI' || zone === 'BENGALURU') {
      return 'METRO';
    }

    if (zone === 'JK' || zone === 'NORTHEAST') {
      return 'REMOTE';
    }

    return zone;
  }

  function rateForZone(zone) {
    return CONFIG.rates[chargeVariantZone(zone)];
  }

  function deliveryDaysForZone(zone) {
    return CONFIG.deliveryDays[zone] || '';
  }

  /* ============================================================
     DELIVERY PRODUCT / VARIANTS
     ============================================================ */

  let deliveryProductPromise = null;

  async function getDeliveryProduct() {
    if (deliveryProductPromise) {
      return deliveryProductPromise;
    }

    deliveryProductPromise = fetch(
      shopifyRoot() +
        'products/' +
        encodeURIComponent(CONFIG.deliveryProductHandle) +
        '.js',
      {
        method: 'GET',
        headers: {
          Accept: 'application/json'
        },
        cache: 'no-store'
      }
    ).then(async function (response) {
      if (!response.ok) {
        throw new Error('DELIVERY_PRODUCT_NOT_FOUND');
      }

      return response.json();
    });

    return deliveryProductPromise;
  }

  function variantMatchesZone(variant, zone) {
    const title = normalize(variant.title);

    return CONFIG.variantNames[zone].some(function (name) {
      return title === normalize(name);
    });
  }

  async function getDeliveryVariant(zone) {
    const product = await getDeliveryProduct();

    if (!product || !Array.isArray(product.variants)) {
      throw new Error('DELIVERY_VARIANTS_NOT_FOUND');
    }

    const variant = product.variants.find(function (item) {
      return variantMatchesZone(item, zone);
    });

    if (!variant) {
      throw new Error('DELIVERY_VARIANT_MISSING_' + zone);
    }

    return variant;
  }

  /* ============================================================
     CART
     ============================================================ */

  async function getCart() {
    const response = await fetch(shopifyRoot() + 'cart.js', {
      method: 'GET',
      headers: {
        Accept: 'application/json'
      },
      cache: 'no-store'
    });

    if (!response.ok) {
      throw new Error('CART_READ_FAILED');
    }

    return response.json();
  }

  function isDeliveryLine(item) {
    if (!item) return false;

    if (
      item.product_handle === CONFIG.deliveryProductHandle ||
      item.handle === CONFIG.deliveryProductHandle
    ) {
      return true;
    }

    /*
     * Fallback: compare against variants loaded from the delivery
     * product. This protects us if the theme doesn't expose a
     * product handle on cart.js.
     */
    return false;
  }

  async function removeDeliveryLines(cart) {
    const deliveryLines = (cart.items || []).filter(isDeliveryLine);

    for (const line of deliveryLines) {
      await fetch(shopifyRoot() + 'cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          id: line.key,
          quantity: 0
        })
      });
    }
  }

  async function setDeliveryCharge(zone) {
    const variant = await getDeliveryVariant(chargeVariantZone(zone));
    const cart = await getCart();

    /*
     * Identify delivery lines by the actual variant IDs.
     */
    const allDeliveryVariantIds = [];

    for (const candidateZone of Object.keys(CONFIG.variantNames)) {
      try {
        const candidate = await getDeliveryVariant(candidateZone);
        allDeliveryVariantIds.push(Number(candidate.id));
      } catch (error) {
        // Ignore other variants while locating existing lines.
      }
    }

    const existingDeliveryLines = (cart.items || []).filter(function (item) {
      return allDeliveryVariantIds.includes(Number(item.variant_id));
    });

    const correctLine = existingDeliveryLines.find(function (item) {
      return Number(item.variant_id) === Number(variant.id);
    });

    /*
     * Remove all incorrect/duplicate delivery lines.
     */
    for (const line of existingDeliveryLines) {
      if (
        !correctLine ||
        line.key !== correctLine.key ||
        Number(line.quantity) !== 1
      ) {
        await fetch(shopifyRoot() + 'cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            id: line.key,
            quantity:
              correctLine && line.key === correctLine.key ? 1 : 0
          })
        });
      }
    }

    /*
     * If correct line already exists, we're finished.
     */
    if (correctLine) {
      return {
        variantId: Number(variant.id),
        rate: rateForZone(zone),
        zone: zone,
        deliveryDays: deliveryDaysForZone(zone)
      };
    }

    /*
     * Add the correct delivery variant once.
     */
    const response = await fetch(shopifyRoot() + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        items: [
          {
            id: Number(variant.id),
            quantity: 1
          }
        ]
      })
    });

    if (!response.ok) {
      throw new Error('DELIVERY_ADD_FAILED');
    }

    return {
      variantId: Number(variant.id),
      rate: rateForZone(zone),
      zone: zone,
      deliveryDays: deliveryDaysForZone(zone)
    };
  }

  /* ============================================================
     CART REFRESH
     ============================================================ */

  /*
   * The most reliable Basic/Rise implementation is to reload the
   * cart page after changing the delivery line. This guarantees
   * Shopify's rendered cart total is recalculated.
   */
  function refreshCartPage() {
    window.location.reload();
  }

  /* ============================================================
     DELIVERY LINE DISPLAY
     ============================================================ */

  function lockAndRenameDeliveryLine() {
    const deliveryRows = document.querySelectorAll(
      'tr.leafup-delivery-line'
    );

    deliveryRows.forEach(function (row) {
      /*
       * Make the delivery-charge product title non-clickable.
       * Rise normally renders product titles as links.
       */
      const titleLinks = row.querySelectorAll(
        'a[href*="/products/leafup-delivery-charge"]'
      );

      titleLinks.forEach(function (link) {
        const span = document.createElement('span');

        span.textContent = 'Delivery Charge';
        span.className = link.className;
        span.style.cursor = 'default';
        span.setAttribute('aria-label', 'Delivery Charge');

        link.replaceWith(span);
      });

      /*
       * Fallback for any delivery-product link that doesn't match
       * the product URL selector.
       */
      row.querySelectorAll('a').forEach(function (link) {
        if (
          link.textContent.trim() === 'LeafUp Delivery Charge' ||
          link.textContent.trim() === 'Delivery Charge'
        ) {
          const span = document.createElement('span');

          span.textContent = 'Delivery Charge';
          span.className = link.className;
          span.style.cursor = 'default';

          link.replaceWith(span);
        }
      });
    });
  }

  /* ============================================================
     DELIVERY TIME DISPLAY
     ============================================================ */

  /*
   * The Delivery Charge variant currently exposes its zone as a
   * Shopify variant option in the cart, e.g.:
   *
   *   Delivery Zone: Delhi NCR
   *
   * We don't want to expose the internal zone to customers.
   * Replace that option visually with the customer-facing delivery
   * estimate. The actual charge/variant logic is unchanged.
   *
   * The calculated delivery days are stored in sessionStorage when
   * the PIN is successfully calculated, so they survive the cart
   * page reload that follows a successful calculation.
   */
  function renderDeliveryTimeInCart() {
    const deliveryDays =
      sessionStorage.getItem('leafup_shipping_days') || '';

    if (!deliveryDays) return;

    const deliveryRows = document.querySelectorAll(
      'tr.leafup-delivery-line'
    );

    deliveryRows.forEach(function (row) {
      /*
       * Rise renders variant options inside .product-option.
       * Only alter the option area of the delivery line.
       */
      const optionElements = Array.from(
        row.querySelectorAll('.product-option')
      );

      if (!optionElements.length) return;

      /*
       * Rise can render the same product-option in multiple cart
       * representations (for example responsive/mobile markup).
       * We only want ONE customer-facing delivery-time line.
       */
      const firstOption = optionElements[0];

      firstOption.textContent = 'Delivery time: ' + deliveryDays;
      firstOption.style.display = '';

      optionElements.slice(1).forEach(function (option) {
        option.style.display = 'none';
      });
    });
  }

  function observeDeliveryLine() {
    renderDeliveryTimeInCart();
    lockAndRenameDeliveryLine();

    /*
     * Shopify/Rise can replace cart HTML after cart updates.
     * Observe the cart table so the customer-facing text and
     * non-clickable delivery title are restored if the section
     * is re-rendered.
     */
    if (window.__leafupDeliveryObserver) return;

    const cartTable = document.querySelector(
      'cart-items, #main-cart-items, .cart-items'
    );

    if (!cartTable) return;

    window.__leafupDeliveryObserver = new MutationObserver(function () {
      renderDeliveryTimeInCart();
      lockAndRenameDeliveryLine();
    });

    window.__leafupDeliveryObserver.observe(cartTable, {
      childList: true,
      subtree: true
    });
  }

  /* ============================================================
     UI
     ============================================================ */

  function addStyles() {
    if (document.getElementById('leafup-shipping-styles')) return;

    const style = document.createElement('style');
    style.id = 'leafup-shipping-styles';

    style.textContent = `
      #leafup-shipping-calculator {
        margin: 20px 0;
        padding: 16px;
        border: 1px solid rgba(0,0,0,.12);
        border-radius: 8px;
        background: #fff;
      }

      #leafup-shipping-calculator .leafup-shipping-title {
        margin: 0 0 10px;
        font-weight: 600;
        font-size: 16px;
      }

      #leafup-shipping-calculator .leafup-shipping-row {
        display: flex;
        gap: 8px;
        align-items: stretch;
      }

      #leafup-shipping-pincode {
        flex: 1;
        min-width: 0;
        height: 44px;
        padding: 0 12px;
        border: 1px solid rgba(0,0,0,.2);
        border-radius: 4px;
        font-size: 16px;
      }

      #leafup-calculate-shipping {
        height: 44px;
        padding: 0 18px;
        border: 0;
        border-radius: 4px;
        cursor: pointer;
        font-size: 15px;
        font-weight: 600;
      }

      #leafup-calculate-shipping:disabled {
        opacity: .6;
        cursor: wait;
      }

      #leafup-shipping-message {
        margin-top: 10px;
        font-size: 14px;
      }

      #leafup-shipping-message.leafup-error {
        color: #b42318;
      }

      #leafup-shipping-message.leafup-success {
        color: #067647;
      }

      #leafup-shipping-amount {
        font-weight: 600;
      }

      /* Delivery Charge is a system-generated cart line.
         Customers must not be able to change or remove it. */
      tr.leafup-delivery-line .quantity-popover > *,
      tr.leafup-delivery-line cart-remove-button {
        display: none !important;
      }

      tr.leafup-delivery-line .cart-item__quantity {
        pointer-events: none;
      }

      /* Checkout remains unavailable until a delivery charge exists. */
      #checkout.leafup-checkout-locked {
        opacity: .55;
        cursor: not-allowed;
      }

      @media (max-width: 480px) {
        #leafup-shipping-calculator .leafup-shipping-row {
          flex-direction: column;
        }

        #leafup-calculate-shipping {
          width: 100%;
        }
      }
    `;

    document.head.appendChild(style);
  }

  function createCalculator() {
    if (document.getElementById(CONFIG.ui.wrapperId)) {
      return document.getElementById(CONFIG.ui.wrapperId);
    }

    /*
     * We only inject this on the cart page.
     * This prevents the calculator appearing on product/collection pages.
     */
    if (!/\/cart(?:\/|$|\?)/.test(window.location.pathname)) {
      return null;
    }

    const wrapper = document.createElement('div');
    wrapper.id = CONFIG.ui.wrapperId;

    wrapper.innerHTML = `
      <p class="leafup-shipping-title">Calculate delivery</p>

      <div class="leafup-shipping-row">
        <input
          id="${CONFIG.ui.inputId}"
          type="text"
          inputmode="numeric"
          autocomplete="postal-code"
          maxlength="6"
          placeholder="Enter your PIN code"
          aria-label="PIN code"
          required
        >

        <button
          id="${CONFIG.ui.buttonId}"
          type="button"
        >
          Calculate
        </button>
      </div>

      <div
        id="${CONFIG.ui.messageId}"
        class="leafup-shipping-message"
        aria-live="polite"
      ></div>
    `;

    return wrapper;
  }

  function insertCalculator(wrapper) {
    if (!wrapper) return;

    /*
     * Rise cart commonly has a cart footer / checkout area.
     * Try several safe insertion points.
     */
    const targets = [
      '.cart__footer',
      '.cart__footer-wrapper',
      '.cart__blocks',
      '[data-cart-footer]',
      'form[action="/cart"]'
    ];

    for (const selector of targets) {
      const target = document.querySelector(selector);

      if (target) {
        target.parentNode.insertBefore(wrapper, target);
        return;
      }
    }

    /*
     * Fallback: place it before the checkout button.
     */
    const checkout = document.querySelector(
      '[name="checkout"], button[type="submit"][name="checkout"], a[href="/checkout"]'
    );

    if (checkout && checkout.parentNode) {
      checkout.parentNode.insertBefore(wrapper, checkout);
      return;
    }

    /*
     * Last fallback: before the footer.
     */
    const footer = document.querySelector('footer');

    if (footer && footer.parentNode) {
      footer.parentNode.insertBefore(wrapper, footer);
    }
  }

  function setMessage(text, type) {
    const element = document.getElementById(CONFIG.ui.messageId);
    if (!element) return;

    element.textContent = text || '';
    element.className =
      'leafup-shipping-message ' +
      (type === 'error' ? 'leafup-error' : 'leafup-success');
  }

  function setLoading(isLoading) {
    const button = document.getElementById(CONFIG.ui.buttonId);

    if (!button) return;

    button.disabled = isLoading;
    button.textContent = isLoading ? 'Checking…' : 'Calculate';
  }

  async function calculate() {
    const input = document.getElementById(CONFIG.ui.inputId);

    if (!input) return;

    const pin = cleanPin(input.value);

    if (!validPinFormat(pin)) {
      setMessage('Please enter a valid 6-digit PIN code.', 'error');
      return;
    }

    setLoading(true);
    setMessage('', '');

    try {
      const lookup = await lookupPin(pin);
      const zone = determineZone(lookup);
      const rate = rateForZone(zone);

      await setDeliveryCharge(zone);

      /*
       * Store the customer's PIN for later use by the checkout/order
       * implementation if required.
       */
      try {
        sessionStorage.setItem('leafup_shipping_pin', pin);
        sessionStorage.setItem('leafup_shipping_zone', zone);
        sessionStorage.setItem('leafup_shipping_rate', String(rate));
        sessionStorage.setItem(
          'leafup_shipping_days',
          deliveryDaysForZone(zone)
        );
      } catch (error) {}

      const deliveryDays = deliveryDaysForZone(zone);

      setMessage(
        'Delivery charge: ' +
          money(rate) +
          (deliveryDays ? ' • Estimated delivery: ' + deliveryDays : '') +
          '. Updating your cart…',
        'success'
      );

      /*
       * Reload so the actual Shopify cart total displays the
       * newly-added delivery-charge line.
       */
      setTimeout(refreshCartPage, 250);
    } catch (error) {
      console.error('[LeafUp Shipping]', error);

      let message =
        'We could not calculate delivery right now. Please try again.';

      if (error.message === 'PIN_NOT_FOUND') {
        message =
          'This PIN code could not be verified. Please check the PIN and try again.';
      }

      if (error.message === 'DELIVERY_PRODUCT_NOT_FOUND') {
        message =
          'Shipping setup is incomplete: the Delivery Charge product is missing.';
      }

      if (
        error.message === 'DELIVERY_VARIANT_MISSING_NCR' ||
        error.message === 'DELIVERY_VARIANT_MISSING_METRO' ||
        error.message === 'DELIVERY_VARIANT_MISSING_DEFAULT' ||
        error.message === 'DELIVERY_VARIANT_MISSING_REMOTE'
      ) {
        message =
          'Shipping setup is incomplete: one Delivery Charge variant is missing.';
      }

      setMessage(message, 'error');
    } finally {
      setLoading(false);
    }
  }

  /*
   * Keep the LeafUp Delivery Charge line at the bottom of the
   * cart item table, immediately before the cart total/footer.
   *
   * Shopify's Ajax Cart API adds a new line to the cart and the
   * Rise cart can render that new line at the top. We therefore
   * move only the delivery row in the DOM; normal product order
   * is untouched.
   */
  function moveDeliveryLineToBottom() {
    const cartItems = document.querySelector('#main-cart-items');
    if (!cartItems) return;

    const tbody = cartItems.querySelector('tbody');
    if (!tbody) return;

    const deliveryLine = tbody.querySelector('tr.leafup-delivery-line');
    if (!deliveryLine) return;

    if (tbody.lastElementChild !== deliveryLine) {
      tbody.appendChild(deliveryLine);
    }
  }

  let deliveryOrderObserver = null;

  function startDeliveryOrderObserver() {
    const cartItems = document.querySelector('#main-cart-items');
    if (!cartItems) return;

    moveDeliveryLineToBottom();

    if (deliveryOrderObserver) {
      deliveryOrderObserver.disconnect();
    }

    deliveryOrderObserver = new MutationObserver(function () {
      moveDeliveryLineToBottom();
    });

    deliveryOrderObserver.observe(cartItems, {
      childList: true,
      subtree: true
    });
  }

  /*
   * Checkout protection
   *
   * A checkout attempt is allowed only when the cart contains one
   * of LeafUp's delivery-charge variants. This protects against:
   *   - opening checkout without calculating a PIN
   *   - manually bypassing the Calculate button
   *   - a stale/removed delivery line
   */
  let deliveryVariantIdsPromise = null;
  let deliveryCheckoutReady = false;

  async function getDeliveryVariantIds() {
    if (!deliveryVariantIdsPromise) {
      deliveryVariantIdsPromise = getDeliveryProduct().then(function (product) {
        return (product.variants || []).map(function (variant) {
          return Number(variant.id);
        });
      });
    }

    return deliveryVariantIdsPromise;
  }

  async function cartHasDeliveryCharge() {
    try {
      const cart = await getCart();
      const variantIds = await getDeliveryVariantIds();

      return (cart.items || []).some(function (item) {
        return variantIds.includes(Number(item.variant_id));
      });
    } catch (error) {
      console.error('[LeafUp Shipping] Could not verify delivery charge:', error);
      return false;
    }
  }

  function checkoutButton() {
    return document.getElementById('checkout');
  }

  function lockCheckoutButton(locked) {
    deliveryCheckoutReady = !locked;

    const button = checkoutButton();
    if (!button) return;

    button.disabled = Boolean(locked);
    button.classList.toggle('leafup-checkout-locked', Boolean(locked));

    if (locked) {
      button.setAttribute(
        'aria-label',
        'Calculate delivery before checkout'
      );
    } else {
      button.removeAttribute('aria-label');
    }
  }

  async function updateCheckoutState() {
    const button = checkoutButton();
    if (!button) return;

    /*
     * Lock immediately while we verify the cart.
     * This prevents a checkout click during the verification window.
     */
    lockCheckoutButton(true);

    const hasDelivery = await cartHasDeliveryCharge();
    lockCheckoutButton(!hasDelivery);
  }

  function showCheckoutMessage() {
    const message = document.getElementById(CONFIG.ui.messageId);
    const input = document.getElementById(CONFIG.ui.inputId);

    if (message) {
      message.textContent =
        'Please enter your PIN code and calculate delivery before checkout.';
      message.className =
        'leafup-shipping-message leafup-error';
    }

    if (input) {
      input.focus();
    }
  }

  function bindCheckoutGuard() {
    const form = document.getElementById('cart');
    const button = checkoutButton();

    if (button && button.dataset.leafupCheckoutBound !== 'true') {
      button.dataset.leafupCheckoutBound = 'true';

      button.addEventListener('click', function (event) {
        /*
         * This check is intentionally synchronous. We must prevent
         * checkout immediately; waiting for an async cart request
         * would be too late for the browser's submit event.
         */
        if (!deliveryCheckoutReady) {
          event.preventDefault();
          event.stopImmediatePropagation();
          showCheckoutMessage();
          lockCheckoutButton(true);
        }
      }, true);
    }

    if (form && form.dataset.leafupCheckoutBound !== 'true') {
      form.dataset.leafupCheckoutBound = 'true';

      form.addEventListener('submit', function (event) {
        const submitter = event.submitter;

        /*
         * Only guard the checkout submission. Normal cart quantity
         * updates must continue to work.
         */
        if (
          submitter &&
          submitter.id &&
          submitter.id !== 'checkout'
        ) {
          return;
        }

        if (!deliveryCheckoutReady) {
          event.preventDefault();
          event.stopImmediatePropagation();
          showCheckoutMessage();
          lockCheckoutButton(true);
        }
      }, true);
    }

    updateCheckoutState();
  }

  function initialize() {
    if (!/\/cart(?:\/|$|\?)/.test(window.location.pathname)) {
      return;
    }

    addStyles();
    startDeliveryOrderObserver();
    bindCheckoutGuard();

    /*
     * main-cart-footer.liquid creates the calculator markup.
     * Therefore the calculator can already exist when this script
     * initializes. The earlier version returned immediately in
     * that situation, leaving the Calculate button unbound.
     *
     * Always find/create the calculator, then bind its controls.
     */
    let calculator = document.getElementById(CONFIG.ui.wrapperId);

    if (!calculator) {
      calculator = createCalculator();

      if (!calculator) return;

      insertCalculator(calculator);
    }

    const button = document.getElementById(CONFIG.ui.buttonId);
    const input = document.getElementById(CONFIG.ui.inputId);

    if (!button || !input) {
      console.warn('[LeafUp Shipping] Calculator controls not found.');
      return;
    }

    /*
     * Prevent duplicate listeners if Shopify/Rise re-renders
     * the cart section.
     */
    if (button.dataset.leafupBound === 'true') {
      return;
    }

    button.dataset.leafupBound = 'true';

    button.addEventListener('click', function (event) {
      event.preventDefault();
      calculate();
    });

    input.addEventListener('keydown', function (event) {
      if (event.key === 'Enter') {
        event.preventDefault();
        calculate();
      }
    });

    input.addEventListener('input', function () {
      input.value = input.value.replace(/\D/g, '').slice(0, 6);
    });

    observeDeliveryLine();
  }

  /*
   * Public API for later integration with Rise cart sections.
   */
  window.LeafUpShipping = {
    lookupPin: lookupPin,
    determineZone: determineZone,
    getDeliveryProduct: getDeliveryProduct,
    getDeliveryVariant: getDeliveryVariant,
    setDeliveryCharge: setDeliveryCharge,
    deliveryDaysForZone: deliveryDaysForZone,
    calculate: calculate,
    config: CONFIG
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    initialize();
  }
})();
